/*
* Component detail view for authenticated users
*/

import * as _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { Button, ButtonGroup, ButtonToolbar, Row, Well } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import * as AppComp from '../common/app-component-base';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';
import { NavigationLinks } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import AppConfiguration from '../config/configuration';
import * as DetailFormCommon from '../inventory/detail-form/common';
import * as ReduxQueries from '../queries';
import * as ComponentCommon from './common';

export interface IComponentDetailProps extends AppComp.IAppComponentProps {
    componentObjId?: any;
    componentDetail?: any;
    componentReplacementCosts?: any;
    navigateTo?: any;
    // newOrder?: any;
    // updateOrder?: any;
    groups?: any;
    // deleteOrder?: any;
}

export interface IComponentDetailState extends AppComp.IAppComponentState {
    filterAssociation?: any;
    filterClient?: any;
    formChanges?: any;
    skipAssociation?: any;
    skipClient?: any;
}

class ComponentDetail extends AppComp.AppComponent<IComponentDetailProps, IComponentDetailState> {
    public MomentDateFormat: string = AppConfiguration.getMomentShortDateFormat();
    public MomentDateTimeFormat: string = AppConfiguration.getMomentDateTimeFormat();
    public originalFormDataCache = {};
    public readonly virtualizedDropdownPageSize: number = 20;

    public kendoFormWhiteBoxSectionProps = {};

    public constructor(props: IComponentDetailProps) {
        super(props, 'app.component-detail.');

        this.state = {
            filterAssociation: undefined,
            filterClient: undefined,
            formChanges: {},
            skipAssociation: 0,
            skipClient: 0,
        };
    }

    public componentDidMount() {
        const enforce = this.enforceAdminOnly();

        if (enforce) {
            this.props.navigateTo(NavigationLinks.userDashboard());
        }
    }

    public componentDidUpdate() {
        let resetFormData = this.getFormChangesResetRequired();

        if (resetFormData && _.size(this.state.formChanges)) {
            this.setState({ formChanges: {} });
        }
    }

    // Data transforms
    public formattedDate(date: any) {
        // Returns a formatted date, or empty

        return date ? moment(new Date(date)).format(this.MomentDateFormat) : '';
    }

    public extendData(component: any) {
        if (!component) { return {}; }

        let record = _.cloneDeep(component);

        return record;
    }

    // Handlers
    // Button handlers
    public handleEditComponentButton(e: any) {
        const { componentObjId, ...rest } = this.props;

        this.props.navigateTo(NavigationLinks.componentEdit(componentObjId));
    }

    public handleFormInputChange(e: any, x: any) {
        // console.log('handleForminputchange', {e: e, x: x });

        const getChangesResult = this.getFormChanges(e, x);
        // console.log('handleForminputchange getChangesResult', {getChangesResult: getChangesResult, e: e, x: x });
        if (getChangesResult.applyChanges) {
            this.setState({
                formChanges: getChangesResult.formChanges
            });
        }
    }

    // Helpers
    public getFormChanges(e: any, x: any) {
        let result = {
            applyChanges: false,
            formChanges: {},
            formChangesResult: {},
        };

        let fieldName = '';
        let newValue;

        let formChangesCopy = _.cloneDeep(this.state.formChanges);

        if (_.get(e, 'target.props.name')) {
            // Kendo form element
            if (_.has(e, 'target.combobox')) {
                // combobox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'target.value');
            } else {
                // assume textbox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'value');
            }
        } else {
            const eventTargetType = _.get(e, 'target.type', '');

            switch (eventTargetType) {
                case 'textarea':
                case 'text':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                case 'kendo-dropdownlist':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value', '');
                    break;

                case 'virtualized-select':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(x, 'value');
                    break;

                case 'delete-button':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                default:
                    // console.log(`*** handleFormInputChange switch hit default for eventTargetType ${eventTargetType}`);
                    break;
            }
        }

        // console.log(`handleFormInputChange continuing 1 fieldName ${fieldName} newValue ${newValue}`, { e: e, x: x });

        if (!fieldName) { return result; }

        let applyChange = false;
        let origVal = _.get(this.originalFormDataCache, fieldName);
        let formChangesResult = {};
        if (_.isArray(origVal)) {
            if (_.isArray(newValue)) {
                if (!_.isEqual(origVal, newValue)) {
                    _.set(formChangesCopy, fieldName, _.cloneDeep(newValue));
                    _.set(formChangesResult, fieldName, _.cloneDeep(newValue));
                    applyChange = true;
                } else {
                    applyChange = false;
                }
            } else {
                if (_.includes(origVal, newValue)) {
                    // console.log(`original array already contained val ${newValue} origVal`, origVal);
                    applyChange = false;
                } else {
                    const formChangesArr = _.get(formChangesCopy, fieldName, []);
                    // console.log(`${fieldName} formChangesArr`, formChangesArr);
                    if (_.includes(formChangesArr, newValue)) {
                        // console.log(`formChangesArr field ${fieldName} array already contains val ${newValue} formChangesArr`, formChangesArr);
                        applyChange = false;
                    } else {
                        formChangesArr.push(newValue);
                        _.set(formChangesCopy, fieldName, _.cloneDeep(formChangesArr));
                        _.set(formChangesResult, fieldName, _.cloneDeep(formChangesArr));
                        applyChange = true;
                        // console.log(`formChangesArr field ${fieldName} array DID NOT have val ${newValue}, applying change`, formChangesArr);
                    }
                }
            }
        } else {
            if (String(newValue) !== String(origVal)) {
                // New value
                _.set(formChangesCopy, fieldName, newValue);
                _.set(formChangesResult, fieldName, newValue);
                applyChange = true;
            } else {
                // Same as orig val
                if (_.has(formChangesCopy, fieldName)) {
                    _.unset(formChangesCopy, fieldName);
                    _.set(formChangesResult, fieldName, newValue);
                    applyChange = true;
                }
            }
        }

        if (applyChange) {
            result.applyChanges = true;
            result.formChanges = _.cloneDeep(formChangesCopy);
            result.formChangesResult = _.cloneDeep(formChangesResult);
        }

        // console.log(`getFormChanges returns result`, result);

        return result;
    }

    public getFormData(componentIn: any, componentReplacementCostsIn: any, groupsIn: any) {
        const component = _.merge(componentIn, this.state.formChanges);

        let resultComponent = ComponentCommon.GetFormDataComponent(component);
        let result = {
            componentObjId: _.get(component, '_id'),
            component: resultComponent,

            componentReplacementCosts: (_.size(componentReplacementCostsIn)) ? componentReplacementCostsIn : [],

            groups: (_.size(groupsIn)) ? groupsIn : [],
        };

        const res = _.merge(result, this.state.formChanges);

        return res;
    }

    public getFormChangesCount() {
        return _.size(this.state.formChanges);
    }

    public getFormChangesResetRequired() {
        let resetFormData = false;
        // Props values
        const pa = Formatters.getInt(this.props, 'componentObjId');

        // Cached values
        const ca = Formatters.getInt(this.originalFormDataCache, 'componentObjId');

        // Determine if formdata state and cache needs reset due to changing associd or invid
        if (pa && ca) {
            resetFormData = (pa !== ca) ? true : false;
        }

        return resetFormData;
    }

    // Renderers
    public renderSectionComponentInformation(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 7 };

        const inputPropsReadonly = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
            rowClassName: 'kendo-form-row-readonly'
        });

        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, {
            className: 'inventory-detail-input-full-width',
        });

        const sectionComponentInformation = (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('component-information')}>
                <KendoWrappers.KendoDisabledInputHidden
                    name="_component.componentId"
                    label={this.appText('component-information.component-id')}
                    value={Formatters.getInt(formData, 'component.componentId')}
                    {...inputPropsReadonly}
                />

                <KendoWrappers.KendoDisabledInputHidden
                    name="_component.componentName"
                    label={this.appText('component-information.component-name')}
                    value={Formatters.getStr(formData, 'component.name')}
                    {...inputPropsReadonly}
                />

                <KendoWrappers.KendoDisabledInputHidden
                    name="_component.componentSummary"
                    label={this.appText('component-information.component-summary')}
                    value={Formatters.getStr(formData, 'component.summary')}
                    {...inputPropsReadonly}
                />

                <KendoWrappers.KendoCheckbox
                    name="_component.inactive"
                    label={this.appText('component-information.component-inactive')}
                    value={formData.component.inactive}
                    disabled={true}
                    readonly={true}
                    {...inputPropsReadonly}
                />

                <DetailFormCommon.InventoryDetailFormSectionSpacing />

                <KendoWrappers.KendoDisabledInputHidden
                    name="_component.categoryId"
                    label={this.appText('component-information.component-category-id')}
                    value={Formatters.getInt(formData, 'component.componentCategoryId')}
                    {...inputPropsReadonly}
                />

                <KendoWrappers.KendoDisabledInputHidden
                    name="_component.category"
                    label={this.appText('component-information.component-category-name')}
                    value={Formatters.getStr(formData, 'component.category')}
                    {...inputPropsReadonly}
                />

                <DetailFormCommon.InventoryDetailFormSectionSpacing />

                <KendoWrappers.KendoDisabledInputHidden
                    name="_component.usefulLife"
                    label={this.appText('component-information.component-useful-life')}
                    value={Formatters.getInt(formData, 'component.usefulLife')}
                    {...inputPropsReadonly}
                />
            </DetailFormCommon.InventoryDetailFormSection>
        );

        const componentLocations = _.map(_.get(formData, 'component.locations', []), (o, i) => {
            return (
                <div
                    key={`component-${Formatters.getStr(formData, 'component.componentId')}-locations-${i}`}
                    className="component-details-location"
                >
                    {o}
                </div>
            );
        });

        const sectionComponentLocations = (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('component-information-locations')}>
                {componentLocations}
            </DetailFormCommon.InventoryDetailFormSection>
        );

        const BuildReplacementCostsComponents = (costs?: Array<any>) => {
            const groups = _.get(formData, 'groups', []);

            return _.map(costs, (o, i) => {
                const group = _.find(groups, { 'groupId': Formatters.getInt(o, 'groupId') });

                let result = _.stubArray();
                result.push(<span key={`component-replacement-costs-${Formatters.getStr(o, '_id')}-${i}-cost`}><strong>{Formatters.formatCurrencyIntl(Formatters.getFloat(o, 'cost'), 2)}</strong></span>);

                if (Formatters.getStr(o, 'explanation')) {
                    result.push(<span key={`component-replacement-costs-${Formatters.getStr(o, '_id')}-${i}-sep1`} className="component-details-replacement-cost-separator"> - </span>);
                    result.push(<span key={`component-replacement-costs-${Formatters.getStr(o, '_id')}-${i}-explanation`} className="component-details-replacement-cost-explanation">{Formatters.getStr(o, 'explanation')}</span>);
                }

                if (Formatters.getStr(o, 'notes')) {
                    result.push(<span key={`component-replacement-costs-${Formatters.getStr(o, '_id')}-${i}-sep2`} className="component-details-replacement-cost-separator"> - </span>);
                    result.push(<span key={`component-replacement-costs-${Formatters.getStr(o, '_id')}-${i}-notes`} className="component-details-replacement-cost-explanation">{Formatters.getStr(o, 'notes')}</span>);
                }

                if (Formatters.getStr(group, 'description')) {
                    result.push(<span key={`component-replacement-costs-${Formatters.getStr(o, '_id')}-${i}-sep3`} className="component-details-replacement-cost-separator"> - </span>);
                    result.push(
                        <span
                            key={`component-replacement-costs-${Formatters.getStr(o, '_id')}-${i}-group`}
                            className="component-details-replacement-cost-group"
                        >
                            ({this.appText('component-replacement-costs.group')} {Formatters.getStr(o, 'groupId')}: {Formatters.getStr(group, 'description')})
                        </span>
                    );
                }

                return (
                    <div className="component-details-replacement-cost-container" key={`component-${Formatters.getStr(formData, 'component.componentId')}-replacement-costs-${i}-${Formatters.getStr(o, '_id')}`}>
                        {result}
                    </div>
                );
            });
        };

        const componentReplacementCostsSorted = _.orderBy(_.get(formData, 'componentReplacementCosts', []), ['replacementCostType.name', 'groupId', 'cost'], ['asc', 'asc', 'asc']);

        const componentReplacementCostsBest = BuildReplacementCostsComponents(_.filter(componentReplacementCostsSorted, (o) => Formatters.getInt(o, 'replacementCostType.replacementCostTypeId') === 1));
        const sectionComponentReplacementCostsBest = (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('component-replacement-costs.best-costs')}>
                {componentReplacementCostsBest}
            </DetailFormCommon.InventoryDetailFormSection>
        );

        const componentReplacementCostsWorst = BuildReplacementCostsComponents(_.filter(componentReplacementCostsSorted, (o) => Formatters.getInt(o, 'replacementCostType.replacementCostTypeId') === 2));
        const sectionComponentReplacementCostsWorst = (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('component-replacement-costs.worst-costs')}>
                {componentReplacementCostsWorst}
            </DetailFormCommon.InventoryDetailFormSection>
        );

        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('component-information')}>
                <Row>
                    <Col xs={5}>
                        {sectionComponentInformation}

                        {sectionComponentLocations}
                    </Col>

                    <Col xs={7}>
                        {sectionComponentReplacementCostsBest}

                        {sectionComponentReplacementCostsWorst}
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderHeaderButtonToolbar = () => {
        let buttonProps = {
            bsSize: 'sm',
            bsStyle: 'info',
        };

        return (
            <div>
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={3}>
                            <ButtonToolbar>
                                <ButtonGroup>
                                    <Button {...buttonProps} onClick={e => { this.handleEditComponentButton(e); }}>
                                        <i className="glyphicon glyphicon-edit" /> {this.appText('edit-component')}
                                    </Button>
                                </ButtonGroup>

                                { /* <ButtonGroup>
                                    <Button {...buttonProps} bsStyle="info" onClick={e => { this.handleCreateOrderForAssocButton(e); }}>
                                            <i className="glyphicon glyphicon-plus" /> {this.appText('new-order')}
                                    </Button>
                                </ButtonGroup>                                   */ }
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public getEditComponentPageTitle(appendStr: string = '', messagePath: string = 'page-title.component-detail', prependPath: boolean = true) {
        return this.appTextPageTitleAppendString(appendStr, messagePath, prependPath);
    }

    public render() {
        const { componentDetail, componentObjId, componentReplacementCosts, groups, ...rest } = this.props;

        const component = this.extendData(componentDetail);
        const componentDocObjId = Formatters.getStr(component, '_id');

        let pageTitle = this.getEditComponentPageTitle('');

        let page = (<div />);

        if (componentObjId && componentDocObjId && componentObjId === componentDocObjId) {
            const formData = this.getFormData(component, componentReplacementCosts, groups);

            pageTitle = this.getEditComponentPageTitle(`${Formatters.getStr(formData, 'component.componentId')} - ${Formatters.getStr(formData, 'component.name')}`);

            // console.log('pageData', {
            //     componentObjId,
            //     component,
            //     componentDetail,
            //     formData,
            //     numChanges: this.getFormChangesCount(),
            //     formChanges: this.state.formChanges,
            // });

            if (this.getFormChangesResetRequired() || !_.size(this.originalFormDataCache)) {
                this.originalFormDataCache = _.cloneDeep(formData);
            }

            page = (
                <div>

                    {this.renderHeaderButtonToolbar()}

                    <Row>
                        <Col xs={12} xl={12}>
                            {/* Component information sections */}
                            <Row>
                                <Col xs={12}>
                                    <div>
                                        {this.renderSectionComponentInformation(formData)}
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>
            );
        }

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = props => ApiEndpoints.getEditComponentDetailUrl(Formatters.getStr(props, 'match.params.componentObjId'));

const mapPropsToConfigs = (props) => ReduxQueries.Queries.getEditComponentDetailRequest(
    getApiUrl(props),
    ReduxQueries.PropsAccessToken(props),
);

const mapStateToProps = (state, props) => {
    const componentObjId = Formatters.getStr(props, 'match.params.componentObjId');

    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(props), state, props, {
        componentObjId: componentObjId,
        componentDetail: state.entities.editComponentDetailComponent,
        componentReplacementCosts: state.entities.editComponentDetailComponentReplacementCosts,
        groups: state.entities.editComponentDetailGroups,
    });
};

const mapDispatchToProps = {
    // updateOrder: MutateActions.updateOrderMutateAsync,
    // deleteOrder: MutateActions.deleteOrderMutateAsync,
    // newOrder: MutateActions.newOrderMutateAsync,
    navigateTo: (path) => push(path),
};

const ComponentDetailContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(ComponentDetail);

export default withRouter(ComponentDetailContainer);



// WEBPACK FOOTER //
// ./src/component/index.tsx